"use strict";
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Painter = void 0;
var SvgRenderingContext_1 = require("../rendering/SvgRenderingContext");
var unrotateShadowOffset_1 = require("../util/unrotateShadowOffset");
var Bounds_1 = require("../Bounds");
var round_1 = require("../util/round");
var isOdd_1 = require("../util/isOdd");
var debug_1 = require("../debug");
/**
 * Given a shadow blur of 20 px, Chrome for example will render dithering well beyond
 * that range, all the way up to 30 px. Subcanvases should be sized to accommodate
 * this extra blur to avoid sudden cutoff.
 */
var shadowDitherSizeFactor = 1.5;
/**
 * Painters implement drawing-related operations for a specific object type.
 */
var Painter = /** @class */ (function () {
    function Painter(type) {
        this.type = type;
        /**
         * Change this value to correct for shadow blur inconsistencies between platforms.
         *
         * The "node-canvas" package, or at least version 1.6.13 of it, appears to interpret the
         * shadow blur value differently from Chrome and ff-canvas. Similar inconsistencies may
         * exist between web browsers. Consequences:
         *
         * 1. The shadow blur appears twice as large as expected
         * 2. The shadow is clipped, since it extends beyond the calculated paint size
         *
         * Example usage:
         *
         * ```
         * if (usingNodeCanvas) {
         *     painters.forEach(p => p.ctxShadowBlurMultiplier = 0.5);
         * }
         * ```
         */
        this.ctxShadowBlurMultiplier = 1.0;
    }
    /**
     * Paints an object on the canvas. {@code state.ctx} must be configured so that [x=0, y=0]
     * corresponds to the center of the canvas. [x=-10, y=10] would be 10 pixels to the
     * left and 10 pixels below the center of the canvas.
     */
    Painter.prototype.paint = function (object, state) {
        if (state.ctx instanceof SvgRenderingContext_1.SvgRenderingContext && this.canWriteSvg(object)) {
            this._writeToMainSvg(object, state);
        }
        else {
            var subcanvas = this._updateSubcanvas(object, state);
            this._placeOntoMainCanvas(subcanvas, object, state);
        }
    };
    Painter.prototype.canWriteSvg = function (object) {
        return false;
    };
    /**
     * Should generate an SVG string and call {@link SvgRenderingContext#unsafeInsertSvg}
     * and any required transform methods. If {@link #canWriteSvg is false}, the
     * node-canvas {@code toBuffer} method is used instead.
     */
    Painter.prototype.writeSvg = function (ctx, store, object) {
    };
    /**
     * Should use {@link ObjectResourceStore.load} to asynchronously load all resources
     * needed to paint the object. When painting, {@link ObjectResourceStore.require} can be
     * used to retrieve the resources and their status.
     */
    Painter.prototype.loadResources = function (store, object) {
    };
    /**
     * Paints an object on its sub-canvas and returns that sub-canvas.
     */
    Painter.prototype._updateSubcanvas = function (newData, state) {
        var _this = this;
        return state.updateSubcanvas(newData, function (previousData, store) {
            if (store.popChanged() || !previousData || _this.shouldRepaint(previousData, newData)) {
                store.layers.result.clear();
                _this.paintSubcanvas(newData, store, state);
            }
        });
    };
    /**
     * Paints an object on {@code store.layers.result}, at a 0-angle and with 100% global alpha.
     */
    Painter.prototype.paintSubcanvas = function (object, store, state) {
        var ctx = store.layers.result.ctx;
        object.stroke && this.paintStroke(store, object, state);
        ctx.save();
        this.translatePastSpacing(ctx, object);
        this.paintFill(store, object);
        ctx.restore();
    };
    /**
     * Places a sub-canvas onto the main canvas, while applying {@code object.alpha}.
     */
    Painter.prototype._placeOntoMainCanvas = function (subcanvas, object, state) {
        var ctx = state.ctx;
        ctx.save();
        this._applyTransforms(state, object);
        this._undoDeviceScales(state);
        var toScreenScale = state.storeOf(object).layers.result.getToScreenScale();
        ctx.drawSubcanvas(subcanvas, 0, 0, object, toScreenScale);
        ctx.restore();
        debug_1.debug.SHOW_BOUNDS && this._drawBounds(object, ctx);
    };
    /**
     * Writes object to main svg.
     */
    Painter.prototype._writeToMainSvg = function (object, state) {
        var store = state.storeOf(object);
        store.layers.resizeTo(state.pipeline.paintSizeOf(object));
        var ctx = state.ctx;
        ctx.save();
        this._applyTransforms(state, object);
        this._undoDeviceScales(state);
        this.writeSvg(ctx, state.storeOf(object), object);
        ctx.restore();
    };
    /**
     * Applies transform for before any object paint
     * {@link RenderingContext.save} must be called before this
     */
    Painter.prototype._applyTransforms = function (state, object) {
        var ctx = state.ctx;
        ctx.translate(round_1.round(object, object.x), round_1.round(object, object.y));
        ctx.rotate(object.angle);
        ctx.translate(round_1.round(object, this.paintOffsetXOf(object)), round_1.round(object, this.paintOffsetYOf(object)));
        ctx.globalAlpha = object.alpha;
    };
    /**
     * Undoes device scales because the subcanvas is already sized accordingly.
     */
    Painter.prototype._undoDeviceScales = function (state) {
        var ctx = state.ctx;
        var scale = state.pipeline.device.transform.scale;
        var pixelRatio = state.pipeline.device.pixelRatio;
        ctx.scale(1 / scale.x, 1 / scale.y);
        ctx.scale(1 / pixelRatio, 1 / pixelRatio);
    };
    /**
     * Debug helper that draws an outline matching the object's boundaries.
     */
    Painter.prototype._drawBounds = function (object, ctx) {
        var bounds = this.boundsOf(object);
        ctx.save();
        ctx.translate(round_1.round(object, bounds.x), round_1.round(object, bounds.y));
        ctx.rotate(bounds.angle);
        ctx.setLineDash([3, 5]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "red";
        ctx.globalAlpha = 0.5;
        ctx.strokeRect(0.5, 0.5, round_1.round(object, bounds.width) - 1, round_1.round(object, bounds.height) - 1);
        ctx.restore();
    };
    /**
     * Applies device scales to context.
     */
    Painter.prototype.applyDeviceScales = function (ctx, device) {
        var scale = device.transform.scale;
        var pixelRatio = device.pixelRatio;
        ctx.scale(scale.x, scale.y);
        ctx.scale(pixelRatio, pixelRatio);
    };
    /**
     * Used to ensure the top-left coordinate of the object points at the object itself and not its shadow.
     */
    Painter.prototype.paintOffsetXOf = function (object) {
        return -1 * round_1.round(object, this.horizontalShadowSpacingOf(object));
    };
    /**
     * Used to ensure the top-left coordinate of the object points at the object itself and not its shadow.
     */
    Painter.prototype.paintOffsetYOf = function (object) {
        return -1 * round_1.round(object, this.verticalShadowSpacingOf(object));
    };
    /**
     * Returns {@code true} if the cached sub-canvas is no longer usable. Accounts for stroke,
     * shadow, paint size, custom data and rounding. Painters override this method to add
     * checks for the properties specific to their object type.
     */
    Painter.prototype.shouldRepaint = function (previousData, newData) {
        return this.hasStrokeChanged(previousData, newData)
            || this.hasShadowChanged(previousData, newData)
            || this.hasPaintSizeChanged(previousData, newData)
            || this.hasCustomDataChanged(previousData, newData)
            || previousData.round !== newData.round;
    };
    /**
     * Returns {@code true} if properties related to the object's stroke have changed.
     */
    Painter.prototype.hasStrokeChanged = function (previousData, newData) {
        return previousData.strokeStyle !== newData.strokeStyle
            || previousData.strokeSize !== newData.strokeSize;
    };
    /**
     * Returns {@code true} if properties related to the object's shadow have changed.
     */
    Painter.prototype.hasShadowChanged = function (previousData, newData) {
        return previousData.shadow !== newData.shadow
            || previousData.shadowBlur !== newData.shadowBlur
            || previousData.shadowColor !== newData.shadowColor
            || previousData.shadowOffsetX !== newData.shadowOffsetX
            || previousData.shadowOffsetY !== newData.shadowOffsetY;
    };
    /**
     * Returns {@code true} if the total size required to paint an object has changed.
     */
    Painter.prototype.hasPaintSizeChanged = function (previousData, newData) {
        return this.paintWidthOf(previousData) !== this.paintWidthOf(newData)
            || this.paintHeightOf(previousData) !== this.paintHeightOf(newData);
    };
    /**
     * Returns {@code true} if watched values under {@link PaintableBase.custom} have changed.
     */
    Painter.prototype.hasCustomDataChanged = function (previousData, newData) {
        var e_1, _a;
        try {
            for (var _b = __values(newData.custom.watch), _c = _b.next(); !_c.done; _c = _b.next()) {
                var key = _c.value;
                if (previousData.custom.values[key] !== newData.custom.values[key]) {
                    return true;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return false;
    };
    /**
     * Returns the total size required to paint an object at a 0-angle, i.e. upright.
     */
    Painter.prototype.paintSizeOf = function (object) {
        return {
            width: this.paintWidthOf(object),
            height: this.paintHeightOf(object),
        };
    };
    /**
     * Returns the total width required to paint an object at a 0-angle, i.e. upright.
     */
    Painter.prototype.paintWidthOf = function (object) {
        return round_1.round(object, this.scaledBaseWidthOf(object) + 2 * this.horizontalSpacingOf(object));
    };
    /**
     * Returns the total height required to paint an object at a 0-angle, i.e. upright.
     */
    Painter.prototype.paintHeightOf = function (object) {
        return round_1.round(object, this.scaledBaseHeightOf(object) + 2 * this.verticalSpacingOf(object));
    };
    /**
     * Returns the horizontal spacing required to paint the stroke/shadow of an object.
     */
    Painter.prototype.horizontalSpacingOf = function (object) {
        return this.strokeSizeOf(object) + this.horizontalShadowSpacingOf(object);
    };
    /**
     * Returns the horizontal spacing required to paint the shadow of an object.
     */
    Painter.prototype.horizontalShadowSpacingOf = function (object) {
        return this.shadowBlurOf(object) * shadowDitherSizeFactor + Math.abs(this.shadowOffsetXOf(object));
    };
    /**
     * Returns the vertical spacing required to paint the stroke/shadow of an object.
     */
    Painter.prototype.verticalSpacingOf = function (object) {
        return this.strokeSizeOf(object) + this.verticalShadowSpacingOf(object);
    };
    /**
     * Returns the vertical spacing required to paint the shadow of an object.
     */
    Painter.prototype.verticalShadowSpacingOf = function (object) {
        return this.shadowBlurOf(object) * shadowDitherSizeFactor + Math.abs(this.shadowOffsetYOf(object));
    };
    /**
     * Returns the size of the shadow blur. 0 if the shadow is inactive.
     */
    Painter.prototype.shadowBlurOf = function (object) {
        return object.shadow ? object.shadowBlur : 0;
    };
    /**
     * Returns the X-offset of the shadow blur. 0 if the shadow is inactive.
     */
    Painter.prototype.shadowOffsetXOf = function (object) {
        return object.shadow ? unrotateShadowOffset_1.unrotateShadowOffset(object).x : 0;
    };
    /**
     * Returns the Y-offset of the shadow blur. 0 if the shadow is inactive.
     */
    Painter.prototype.shadowOffsetYOf = function (object) {
        return object.shadow ? unrotateShadowOffset_1.unrotateShadowOffset(object).y : 0;
    };
    /**
     * Returns the size of an object's stroke. 0 if the stroke is inactive.
     */
    Painter.prototype.strokeSizeOf = function (object) {
        return object.stroke ? object.strokeSize : 0;
    };
    /**
     * Returns the scaled width of an object, excluding stroke/shadow.
     */
    Painter.prototype.scaledBaseWidthOf = function (object) {
        return this.baseWidthOf(object) * object.scaleX;
    };
    /**
     * Returns the scaled height of an object, excluding stroke/shadow.
     */
    Painter.prototype.scaledBaseHeightOf = function (object) {
        return this.baseHeightOf(object) * object.scaleY;
    };
    /**
     * Returns the scaled size of an object, excluding stroke/shadow.
     */
    Painter.prototype.scaledBaseSizeOf = function (object) {
        return {
            width: this.scaledBaseWidthOf(object),
            height: this.scaledBaseHeightOf(object)
        };
    };
    /**
     * For sub-canvases. Makes [x=0, y=0] match the top-left of the object, past its shadow/stroke.
     */
    Painter.prototype.translatePastSpacing = function (ctx, object) {
        ctx.translate(round_1.round(object, this.horizontalSpacingOf(object)), round_1.round(object, this.verticalSpacingOf(object)));
    };
    /**
     * Indicates whether an object has a shadow that is offset in any direction.
     */
    Painter.prototype.hasOffsetShadow = function (object) {
        return object.shadow && (object.shadowOffsetX !== 0 || object.shadowOffsetY !== 0);
    };
    /**
     * Applies the shadow settings of an object to subsequent draw operations.
     */
    Painter.prototype.applyShadowSettings = function (ctx, object) {
        if (!object.shadow) {
            this.removeShadowSettings(ctx);
            return;
        }
        var _a = unrotateShadowOffset_1.unrotateShadowOffset(object), offsetX = _a.x, offsetY = _a.y;
        ctx.shadowBlur = round_1.round(object, object.shadowBlur * this.ctxShadowBlurMultiplier);
        ctx.shadowColor = object.shadowColor;
        ctx.shadowOffsetX = round_1.round(object, offsetX);
        ctx.shadowOffsetY = round_1.round(object, offsetY);
    };
    /**
     * Removes previously applied shadow settings from subsequent draw operations.
     */
    Painter.prototype.removeShadowSettings = function (ctx) {
        ctx.shadowBlur = 0;
        ctx.shadowColor = "transparent";
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
    };
    /**
     * Applies the stroke settings of an object to subsequent draw operations.
     */
    Painter.prototype.applyStrokeSettings = function (ctx, object) {
        if (!object.stroke) {
            this.removeStrokeSettings(ctx);
            return;
        }
        ctx.strokeStyle = object.strokeStyle;
        ctx.lineWidth = this.strokePaintSizeOf(object);
    };
    /**
     * Removes previously applied stroke settings from subsequent draw operations.
     */
    Painter.prototype.removeStrokeSettings = function (ctx) {
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 0;
    };
    /**
     * Returns the width/height required to paint the stroke of an object.
     */
    Painter.prototype.strokePaintSizeOf = function (object) {
        return object.stroke ? 2 * round_1.round(object, object.strokeSize) : 0;
    };
    /**
     * Ensures that strokes are properly aligned to the pixel grid.
     *
     * See also: http://usefulangle.com/post/17/html5-canvas-drawing-1px-crisp-straight-lines
     */
    Painter.prototype.translateForCrispLine = function (ctx) {
        if (isOdd_1.isOdd(ctx.lineWidth)) {
            ctx.translate(-0.5, -0.5);
        }
    };
    /**
     * Returns the boundaries that should used for GUI purposes (e.g. for resize handles).
     */
    Painter.prototype.boundsOf = function (object) {
        var x = round_1.round(object, object.x);
        var y = round_1.round(object, object.y);
        var baseWidth = round_1.round(object, this.scaledBaseWidthOf(object));
        var baseHeight = round_1.round(object, this.scaledBaseHeightOf(object));
        var strokeSize = this.strokePaintSizeOf(object);
        return new Bounds_1.Bounds(x, y, baseWidth + strokeSize, baseHeight + strokeSize, object.angle);
    };
    /**
     * Requires the resources of `object` to be loaded.
     *
     * Returns the max paint operation surface of the paintable.
     */
    Painter.prototype.maxPaintOperationSurfaceOf = function (object, store) {
        var device = store.layers.device;
        var sizes = this
            .paintOperationSizesOf(object, store)
            .map(function (s) { return device.toVirtualRenderResolution(s); });
        return {
            width: Math.max.apply(Math, __spread(sizes.map(function (s) { return s.width; }))),
            height: Math.max.apply(Math, __spread(sizes.map(function (s) { return s.height; }))),
            area: Math.max.apply(Math, __spread(sizes.map(function (s) { return s.width * s.height; })))
        };
    };
    /**
     * Returns the size of each paint operation required to draw the object. (This can be
     * larger than the actual paint size in some cases, like cropped images.)
     */
    Painter.prototype.paintOperationSizesOf = function (object, store) {
        return [this.paintSizeOf(object)];
    };
    return Painter;
}());
exports.Painter = Painter;
//# sourceMappingURL=Painter.js.map